import { z } from 'zod';
import { posAttachmentListSchema } from './attachments';

/**
 * Task #3131 — Trasferimenti inter-magazzino a 4 fasi.
 *
 * Richiedi → Autorizza → Invia → Carica. Union type condivisi FE/BE per stati
 * pratica, flussi operativi e modi di selezione riga. La macchina a stati è
 * qui (SoT); il backend la applica con guardie FOR UPDATE, il FE la usa per
 * il gating delle azioni contestuali.
 */

/** Stato della pratica di trasferimento. */
export type TransferStatus =
  | 'requested'   // richiesta creata, in attesa di autorizzazione
  | 'authorized'  // autorizzata (anche parzialmente riga per riga), merce riservata
  | 'in_transit'  // spedita: DDT emesso, merce in transito
  | 'completed'   // caricata a destinazione (totale o parziale con discrepanze)
  | 'rejected'    // richiesta rifiutata in autorizzazione
  | 'cancelled'   // annullata prima del completamento
  | 'returned';   // trasferimento fallito, merce rientrata all'origine

export const TRANSFER_STATUSES: readonly TransferStatus[] = [
  'requested', 'authorized', 'in_transit', 'completed', 'rejected', 'cancelled', 'returned',
];

export function isTransferStatus(v: unknown): v is TransferStatus {
  return typeof v === 'string' && (TRANSFER_STATUSES as readonly string[]).includes(v);
}

/**
 * Task #4432 (PM-022/PM-023) — Esito quantitativo della pratica, ORTOGONALE
 * allo stato (gli stati restano invariati). Derivato dalle righe:
 *  - `partially_authorized`: almeno una riga con `authorized_qty < requested_qty`
 *    e almeno una quantità autorizzata > 0 (il rifiuto totale è lo stato
 *    `rejected`, non un esito parziale);
 *  - `short_received`: ricezione eseguita (`received_at`) con almeno una riga
 *    `received_qty < sent_qty` (ammanco in transito, movimento dedicato);
 *  - `partially_authorized_short_received`: entrambi;
 *  - `full`: nessuna differenza (o pratica non ancora autorizzata).
 * La quota non autorizzata resta leggibile come residuo (requested − authorized
 * con `auth_reject_reason`): nessun backorder automatico.
 */
export type TransferOutcome =
  | 'full'
  | 'partially_authorized'
  | 'short_received'
  | 'partially_authorized_short_received';

export const TRANSFER_OUTCOMES: readonly TransferOutcome[] = [
  'full', 'partially_authorized', 'short_received', 'partially_authorized_short_received',
];

export function resolveTransferOutcome(flags: { partially_authorized: boolean; short_received: boolean }): TransferOutcome {
  if (flags.partially_authorized && flags.short_received) return 'partially_authorized_short_received';
  if (flags.partially_authorized) return 'partially_authorized';
  if (flags.short_received) return 'short_received';
  return 'full';
}

export function isPartialTransferOutcome(outcome: TransferOutcome): boolean {
  return outcome !== 'full';
}

/** Filtro lista: `partial` = qualunque esito diverso da `full`. */
export const TRANSFER_OUTCOME_FILTERS = ['partial', 'full', 'partially_authorized', 'short_received'] as const;
export type TransferOutcomeFilter = typeof TRANSFER_OUTCOME_FILTERS[number];

/**
 * Ammanco all'arrivo = perdita in transito a carico del trasporto, NON
 * rettifica (`adjustment`) del magazzino di destinazione. Movimento dedicato
 * di `pos_stock_movements`: `delta_signed = 0` (le giacenze restano quelle
 * della ricezione: origine scarica lo spedito, destinazione carica il
 * ricevuto), `warehouse_id` = origine, `to_warehouse_id` = destinazione,
 * riferito alla pratica (`reference_type`), riga sorgente = riga trasferimento.
 */
export const POS_TRANSIT_LOSS_MOVEMENT_TYPE = 'transit_loss';
export const POS_TRANSIT_LOSS_REFERENCE_TYPE = 'transfer_transit_loss';

/**
 * Flusso operativo della pratica:
 *  · A — ogni fase separata (richiedi / autorizza / invia / carica);
 *  · B — richiedi+autorizza+invia in un solo passo, carico separato;
 *  · C — azione unica (tutte le fasi in un colpo, carico incluso).
 */
export type TransferFlow = 'A' | 'B' | 'C';

export const TRANSFER_FLOWS: readonly TransferFlow[] = ['A', 'B', 'C'];

export function isTransferFlow(v: unknown): v is TransferFlow {
  return typeof v === 'string' && (TRANSFER_FLOWS as readonly string[]).includes(v);
}

/** Transizioni di stato ammesse per la pratica (SoT macchina a stati). */
export const TRANSFER_STATUS_TRANSITIONS: Readonly<Record<TransferStatus, readonly TransferStatus[]>> = {
  requested:  ['authorized', 'rejected', 'cancelled'],
  authorized: ['in_transit', 'cancelled'],
  in_transit: ['completed', 'returned', 'cancelled'],
  completed:  [],
  rejected:   [],
  cancelled:  [],
  returned:   [],
};

/**
 * Modo di selezione dell'inventario per riga:
 *  · serial  — seriali specifici vincolanti al picking (serializzati unique);
 *  · lot     — lotto specifico;
 *  · layer   — strato di carico (DDT + data carico) per il no-lotto;
 *  · generic — quantità generica (MAI per prodotti serializzati unique).
 */
export type TransferSelectionMode = 'serial' | 'lot' | 'layer' | 'generic';

export const TRANSFER_SELECTION_MODES: readonly TransferSelectionMode[] = [
  'serial', 'lot', 'layer', 'generic',
];

export function isTransferSelectionMode(v: unknown): v is TransferSelectionMode {
  return typeof v === 'string' && (TRANSFER_SELECTION_MODES as readonly string[]).includes(v);
}

/** Riferimento allo strato di carico per selection_mode='layer'. */
export const TransferLayerRefSchema = z.object({
  source_doc_id: z.string().uuid().nullable().optional().default(null),
  source_doc_ref: z.string().max(255).nullable().optional().default(null),
  loaded_at: z.string().nullable().optional().default(null),
});

export type TransferLayerRef = z.infer<typeof TransferLayerRefSchema>;

/** Payload di una riga richiesta per il trasferimento. */
export const TransferLineInputSchema = z.object({
  product_id: z.string().uuid(),
  variant_id: z.string().uuid().nullable().optional(),
  /** Exact documentary identity. Internal catalogue SKU is never accepted here. */
  supplier_id: z.string().uuid().nullable().optional(),
  supplier_sku: z.string().trim().min(1).max(255).nullable().optional(),
  lot_id: z.string().uuid().nullable().optional(),
  selection_mode: z.enum(['serial', 'lot', 'layer', 'generic']).optional(),
  layer_ref: TransferLayerRefSchema.nullable().optional(),
  requested_qty: z.number().positive(),
  unit_cost: z.number().nonnegative().nullable().optional(),
  serials: z.array(z.string().min(1)).max(1000).optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export type TransferLineInput = z.infer<typeof TransferLineInputSchema>;

/** Payload HTTP per aprire una richiesta di trasferimento. */
export const TransferCreateInputSchema = z.object({
  flow: z.enum(['A', 'B', 'C']),
  from_warehouse_id: z.string().uuid(),
  to_warehouse_id: z.string().uuid(),
  expected_arrival: z.string().nullable().optional(),
  carrier_name: z.string().max(255).nullable().optional(),
  carrier_plate: z.string().max(64).nullable().optional(),
  tracking_number: z.string().max(255).nullable().optional(),
  notes: z.string().max(4000).nullable().optional(),
  items: z.array(TransferLineInputSchema).min(1).max(500),
});
export type TransferCreateInput = z.infer<typeof TransferCreateInputSchema>;

export const TransferAuthorizeInputSchema = z.object({
  items: z.array(z.object({
    transfer_item_id: z.string().uuid(),
    authorized_qty: z.number().nonnegative(),
    auth_reject_reason: z.string().max(2000).nullable().optional(),
  })).max(500).optional(),
});
export type TransferAuthorizeInput = z.infer<typeof TransferAuthorizeInputSchema>;

export const TransferSendInputSchema = z.object({
  carrier_name: z.string().max(255).nullable().optional(),
  carrier_plate: z.string().max(64).nullable().optional(),
  tracking_number: z.string().max(255).nullable().optional(),
  packages: z.number().int().min(1).max(100000).nullable().optional(),
  weight_kg: z.number().nonnegative().max(9999999).nullable().optional(),
});
export type TransferSendInput = z.infer<typeof TransferSendInputSchema>;

export const TransferReceiveInputSchema = z.object({
  items: z.array(z.object({
    transfer_item_id: z.string().uuid(),
    received_qty: z.number().nonnegative(),
    diff_reason: z.string().max(2000).nullable().optional(),
    serials: z.array(z.string().min(1)).max(1000).optional(),
  })).min(1).max(500),
  generate_ddt_passivo: z.boolean().optional(),
  notes: z.string().max(4000).nullable().optional(),
});
export type TransferReceiveInput = z.infer<typeof TransferReceiveInputSchema>;

export const TransferReasonInputSchema = z.object({
  reason: z.string().min(1).max(2000),
});
export type TransferReasonInput = z.infer<typeof TransferReasonInputSchema>;

export const TransferCancelInputSchema = z.object({
  reason: z.string().max(2000).optional(),
});
export type TransferCancelInput = z.infer<typeof TransferCancelInputSchema>;

/** Query HTTP per la lista delle pratiche, incluso il flag archivio. */
export const TransferListInputSchema = z.object({
  status: z.enum(['requested', 'authorized', 'in_transit', 'completed', 'rejected', 'cancelled', 'returned']).optional(),
  flow: z.enum(['A', 'B', 'C']).optional(),
  from_warehouse_id: z.string().uuid().optional(),
  to_warehouse_id: z.string().uuid().optional(),
  from_date: z.string().optional(),
  to_date: z.string().optional(),
  q: z.string().max(255).optional(),
  /** Esito parziale (Task #4432): `partial` = qualunque esito ≠ `full`. */
  outcome: z.enum(TRANSFER_OUTCOME_FILTERS).optional(),
  include_archived: z.enum(['true', 'false']).transform((value) => value === 'true').optional(),
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
});
export type TransferListInput = z.infer<typeof TransferListInputSchema>;

export const TransferEventsInputSchema = z.object({
  phase: z.enum(['requested', 'authorized', 'sent', 'received', 'rejected', 'cancelled', 'returned']).optional(),
  user_id: z.string().uuid().optional(),
  warehouse_id: z.string().uuid().optional(),
  q: z.string().max(255).optional(),
  date_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  date_to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
});
export type TransferEventsInput = z.infer<typeof TransferEventsInputSchema>;

/**
 * Riferimento allo strato di carico per selection_mode='layer'.
 *
 * @deprecated Derivato da {@link TransferLayerRefSchema}; mantenuto qui come
 * descrizione dei campi wire per i consumer esistenti.
 */
export interface LegacyTransferLayerRefDescription {
  /** Id del DDT passivo / documento di carico dello strato. */
  source_doc_id: string | null;
  /** Riferimento leggibile del documento (es. numero DDT fornitore). */
  source_doc_ref: string | null;
  /** Data di carico dello strato (ISO date). */
  loaded_at: string | null;
}

/** Fasi operative della pratica (allineate a `TRANSFER_PHASE_PERMISSIONS`). */
export type TransferPhase = 'request' | 'authorize' | 'send' | 'load';

export const TRANSFER_PHASES: readonly TransferPhase[] = ['request', 'authorize', 'send', 'load'];

/**
 * Fasi eseguite nello stesso timing alla creazione, per flusso (SoT gating):
 * creare una pratica richiede il permesso di OGNI fase che il flusso esegue
 * in un colpo solo, oltre al permesso di flusso. A esegue solo la richiesta
 * (le altre fasi hanno timing separati), B arriva fino all'invio, C include
 * anche il carico.
 */
export const TRANSFER_FLOW_SAME_TIMING_PHASES: Readonly<Record<TransferFlow, readonly TransferPhase[]>> = {
  A: ['request'],
  B: ['request', 'authorize', 'send'],
  C: ['request', 'authorize', 'send', 'load'],
};

/** Permessi di fase e di flusso (codici catalogo `public.permissions`). */
export const TRANSFER_PHASE_PERMISSIONS: Readonly<Record<TransferPhase, string>> = {
  request: 'transfer.request',
  authorize: 'transfer.authorize',
  send: 'transfer.send',
  load: 'transfer.load',
};

export const TRANSFER_FLOW_PERMISSIONS: Readonly<Record<TransferFlow, string>> = {
  A: 'transfer.flow_a',
  B: 'transfer.flow_b',
  C: 'transfer.flow_c',
};

/**
 * Task #3138 — Permesso dedicato per la dash derivata "Eventi trasferimenti"
 * (unpivot degli attori/timestamp di fase su `pos_transfers`).
 */
export const TRANSFER_AUDIT_VIEW_PERMISSION = 'transfer.audit_view';

/** Eventi di fase mostrati nella dash audit (unpivot colonne `*_by`/`*_at`). */
export const TRANSFER_EVENT_PHASES = [
  'requested', 'authorized', 'sent', 'received', 'rejected', 'cancelled', 'returned',
] as const;

export type TransferEventPhase = (typeof TRANSFER_EVENT_PHASES)[number];

/**
 * Task #3139 — Esito del gating scope sulla sede del magazzino SORGENTE
 * (mapping `pos_warehouses.store_id` ∪ `pos_warehouse_stores`). Calcolato dal
 * backend sulla GET pratica e ri-enforced sulle transizioni authorize/reject:
 * scope presente vincola anche gli admin; admin senza scope → libero;
 * non-admin senza scope → fail-closed; super_admin/master esenti.
 */
export const TransferAuthorizeScopeResponseSchema = z.object({
  allowed: z.boolean(),
  /** Motivo del diniego (null se allowed). */
  reason: z.string().nullable(),
});
export type TransferAuthorizeScope = z.infer<typeof TransferAuthorizeScopeResponseSchema>;

// ──────────────────────── Risposte API trasferimenti ────────────────────────

/** Timestamp wire: PostgreSQL può fornire Date prima della serializzazione HTTP. */
const transferWireTimestampSchema = z
  .union([z.string(), z.date()])
  .transform((value) => value instanceof Date ? value.toISOString() : value);

/**
 * Campi stabili restituiti dopo una mutazione. Il service può avere ulteriori
 * colonne DB, ma il boundary HTTP espone questa forma minima e verificata.
 */
export const TransferMutationResponseSchema = z.object({
  id: z.string(),
  transfer_number: z.string(),
  flow: z.enum(['A', 'B', 'C']),
  status: z.enum(['requested', 'authorized', 'in_transit', 'completed', 'rejected', 'cancelled', 'returned']),
  from_warehouse_id: z.string(),
  to_warehouse_id: z.string(),
  /** RS emittente, pinnata dal magazzino di origine alla creazione (immutabile). */
  company_id: z.string(),
  /** RS di destinazione, pinnata dal magazzino di arrivo alla creazione (immutabile). */
  to_company_id: z.string(),
  /** Trasferimento tra ragioni sociali diverse: base per la fatturazione intragruppo. */
  is_intercompany: z.boolean(),
  /**
   * true = `to_company_id` inferito dalla RS corrente del magazzino di arrivo
   * (pratica storica precedente alla traccia), non pinnato alla creazione.
   */
  to_company_backfilled: z.boolean(),
  ddt_attivo_id: z.string().nullable(),
  ddt_passivo_id: z.string().nullable(),
});
export type TransferMutationResponse = z.infer<typeof TransferMutationResponseSchema>;

/** Ragioni sociali di origine/destinazione risolte per la lettura (nome legale). */
export const TransferCompanyNamesSchema = z.object({
  from_company_name: z.string().nullable(),
  to_company_name: z.string().nullable(),
});

/**
 * Esito quantitativo (Task #4432) esposto su lista e dettaglio. Le somme sono
 * calcolate sulle righe: `unauthorized_qty` = Σ(requested − authorized) sulle
 * righe autorizzate parzialmente; `short_qty` = Σ(sent − received) dopo la
 * ricezione (0 finché la pratica non è stata caricata).
 */
export const TransferOutcomeFieldsSchema = z.object({
  outcome: z.enum(['full', 'partially_authorized', 'short_received', 'partially_authorized_short_received']),
  unauthorized_qty: z.number(),
  short_qty: z.number(),
});
export type TransferOutcomeFields = z.infer<typeof TransferOutcomeFieldsSchema>;

export const TransferListItemResponseSchema = TransferMutationResponseSchema.extend({
  ...TransferCompanyNamesSchema.shape,
  from_warehouse_name: z.string().nullable(),
  to_warehouse_name: z.string().nullable(),
  created_by_name: z.string().nullable(),
  ddt_number: z.string().nullable(),
  items_count: z.number(),
  total_qty: z.union([z.number(), z.string()]),
  created_at: transferWireTimestampSchema,
  /**
   * Il magazzino di ORIGINE rientra nel recinto del chiamante: il FE mostra
   * «Autorizza»/«Rifiuta» solo se `true` (ri-enforced server-side sulle
   * rotte di fase). Calcolato per richiesta dal gate recinto magazzini.
   */
  source_in_scope: z.boolean(),
  ...TransferOutcomeFieldsSchema.shape,
});
export type TransferListItemResponse = z.infer<typeof TransferListItemResponseSchema>;

export const TransferPaginationResponseSchema = z.object({
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});
export type TransferPaginationResponse = z.infer<typeof TransferPaginationResponseSchema>;

/** Risultato interno del service, prima dell'envelope HTTP paginato canonico. */
export const TransferListServiceResultSchema = z.object({
  items: z.array(TransferListItemResponseSchema),
  ...TransferPaginationResponseSchema.shape,
});
export type TransferListServiceResult = z.infer<typeof TransferListServiceResultSchema>;

/** `data` dell'envelope generato da `paginatedResponse`. */
export const TransferListResponseSchema = z.object({
  items: z.array(TransferListItemResponseSchema),
  pagination: TransferPaginationResponseSchema,
});
export type TransferListResponse = z.infer<typeof TransferListResponseSchema>;

export const TransferSerialResponseSchema = z.object({
  id: z.string(),
  transfer_item_id: z.string(),
  serial_number: z.string(),
  received: z.boolean(),
  created_at: transferWireTimestampSchema,
  available: z.boolean().nullable().optional(),
});
export type TransferSerialResponse = z.infer<typeof TransferSerialResponseSchema>;

export const TransferItemResponseSchema = z.object({
  id: z.string(),
  product_id: z.string(),
  product_name: z.string().nullable(),
  product_name_joined: z.string().nullable().optional(),
  sku: z.string().nullable().optional(),
  supplier_id: z.string().nullable().optional(),
  supplier_sku: z.string().nullable().optional(),
  variant_id: z.string().nullable(),
  lot_id: z.string().nullable(),
  lot_number: z.string().nullable().optional(),
  serial_mode: z.enum(['none', 'unique', 'non_unique']),
  selection_mode: z.enum(['serial', 'lot', 'layer', 'generic']),
  requested_qty: z.union([z.number(), z.string()]),
  authorized_qty: z.union([z.number(), z.string()]).nullable(),
  sent_qty: z.union([z.number(), z.string()]),
  received_qty: z.union([z.number(), z.string()]),
  unit_cost: z.union([z.number(), z.string()]).nullable(),
  cost_snapshot: z.union([z.number(), z.string()]).nullable(),
  auth_reject_reason: z.string().nullable().optional(),
  diff_reason: z.string().nullable().optional(),
  available_qty: z.union([z.number(), z.string()]).nullable().optional(),
  /** Residuo non autorizzato (requested − authorized); `null` finché la riga non è stata valutata. */
  unauthorized_qty: z.number().nullable(),
  /** Ammanco all'arrivo (sent − received); `null` finché la pratica non è stata ricevuta. */
  short_qty: z.number().nullable(),
  /** Movimento «perdita in transito» generato dall'ammanco (navigabile negli eventi logistici). */
  transit_loss_movement_id: z.string().nullable(),
  serials: z.array(TransferSerialResponseSchema),
});
export type TransferItemResponse = z.infer<typeof TransferItemResponseSchema>;

export const TransferDetailResponseSchema = TransferMutationResponseSchema.extend({
  ...TransferCompanyNamesSchema.shape,
  ddt_number: z.string().nullable(),
  from_warehouse_name: z.string().nullable(),
  to_warehouse_name: z.string().nullable(),
  created_by_name: z.string().nullable(),
  carrier_name: z.string().nullable(),
  carrier_plate: z.string().nullable(),
  tracking_number: z.string().nullable(),
  notes: z.string().nullable(),
  created_at: transferWireTimestampSchema,
  expected_arrival: transferWireTimestampSchema.nullable(),
  authorized_at: transferWireTimestampSchema.nullable(),
  shipped_at: transferWireTimestampSchema.nullable(),
  received_at: transferWireTimestampSchema.nullable(),
  rejected_at: transferWireTimestampSchema.nullable().optional(),
  cancelled_at: transferWireTimestampSchema.nullable().optional(),
  returned_at: transferWireTimestampSchema.nullable().optional(),
  reject_reason: z.string().nullable().optional(),
  cancel_reason: z.string().nullable().optional(),
  return_reason: z.string().nullable().optional(),
  attachments: posAttachmentListSchema.optional(),
  ...TransferOutcomeFieldsSchema.shape,
  items: z.array(TransferItemResponseSchema),
});
export type TransferDetailResponse = z.infer<typeof TransferDetailResponseSchema>;

export const TransferDetailWithAuthorizeScopeResponseSchema = TransferDetailResponseSchema.extend({
  authorize_scope: TransferAuthorizeScopeResponseSchema,
});
export type TransferDetailWithAuthorizeScopeResponse = z.infer<typeof TransferDetailWithAuthorizeScopeResponseSchema>;

export const TransferDestinationScopeResponseSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('open') }),
  z.object({ mode: z.literal('closed') }),
  z.object({ mode: z.literal('restricted'), warehouse_ids: z.array(z.string().uuid()) }),
]);
export type TransferDestinationScopeResponse = z.infer<typeof TransferDestinationScopeResponseSchema>;

/**
 * Origini selezionabili per una richiesta (GET /pos/transfers/origin-options):
 * tutti i magazzini attivi del tenant; `in_scope` = nel recinto del chiamante.
 * Flusso A: qualsiasi origine (autorizza l'origine); flussi B/C: solo in recinto.
 */
export const TransferOriginOptionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  code: z.string().nullable(),
  store_id: z.string().uuid().nullable(),
  in_scope: z.boolean(),
});
export type TransferOriginOption = z.infer<typeof TransferOriginOptionSchema>;
export const TransferOriginOptionsResponseSchema = z.array(TransferOriginOptionSchema);

/**
 * Task #4302 — Preflight richiesta: quanti riceverebbero la prossima fase
 * pendente (giro operativo) e quanti solo per conoscenza, dalla configurazione
 * Instradamento → Logistica → Trasferimento interno. `phase` null = il flusso
 * nasce già concluso (C): nessuna fase pendente.
 */
export const TransferNextPhaseAudienceInputSchema = z.object({
  flow: z.enum(['A', 'B', 'C']),
  from_warehouse_id: z.string().uuid(),
  to_warehouse_id: z.string().uuid(),
});
export type TransferNextPhaseAudienceInput = z.infer<typeof TransferNextPhaseAudienceInputSchema>;

export const TransferNextPhaseAudienceResponseSchema = z.object({
  phase: z.enum(['authorize', 'send', 'load']).nullable(),
  recipient_count: z.number().int().nonnegative(),
  informed_count: z.number().int().nonnegative(),
});
export type TransferNextPhaseAudienceResponse = z.infer<typeof TransferNextPhaseAudienceResponseSchema>;

export const TransferEventResponseSchema = z.object({
  event_id: z.string(),
  transfer_id: z.string(),
  transfer_number: z.string(),
  flow: z.enum(['A', 'B', 'C']),
  status: z.enum(['requested', 'authorized', 'in_transit', 'completed', 'rejected', 'cancelled', 'returned']),
  phase: z.enum(['requested', 'authorized', 'sent', 'received', 'rejected', 'cancelled', 'returned']),
  occurred_at: transferWireTimestampSchema,
  user_id: z.string().nullable(),
  user_name: z.string().nullable(),
  from_warehouse_id: z.string().nullable(),
  to_warehouse_id: z.string().nullable(),
  from_warehouse_name: z.string().nullable(),
  to_warehouse_name: z.string().nullable(),
  notes: z.string().nullable(),
});
export type TransferEventResponse = z.infer<typeof TransferEventResponseSchema>;

/** Risultato interno del service eventi, prima dell'envelope HTTP paginato. */
export const TransferEventsServiceResultSchema = z.object({
  items: z.array(TransferEventResponseSchema),
  ...TransferPaginationResponseSchema.shape,
});
export type TransferEventsServiceResult = z.infer<typeof TransferEventsServiceResultSchema>;

/** `data` dell'envelope generato da `paginatedResponse` per gli eventi. */
export const TransferEventsResponseSchema = z.object({
  items: z.array(TransferEventResponseSchema),
  pagination: TransferPaginationResponseSchema,
});
export type TransferEventsResponse = z.infer<typeof TransferEventsResponseSchema>;

export const TransferEventLookupsResponseSchema = z.object({
  users: z.array(z.object({
    id: z.string(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    email: z.string().nullable(),
  })),
  warehouses: z.array(z.object({
    id: z.string(),
    name: z.string(),
  })),
});
export type TransferEventLookupsResponse = z.infer<typeof TransferEventLookupsResponseSchema>;
